import { reactive } from 'cdui-js';
import { getEffectiveSafeBottom } from '../../../../stores/common';
import {
  canUseKeyboardAvoidJs,
  isAndroidWebViewKeyboardUnreliable,
} from './keyboardCapability';
import { isOpenProgressViewportLockActive } from './viewportLock';

/**
 * 键盘避让（js-measure / viewport-lock）。
 *
 * Android < M139：官方 IME 不缩 vv → 不采信 overlap，聚焦即开键盘，
 * 半屏 padding + scrollTop 主滚；遮挡键盘下 nearest 常无效，改用 center 兜底。
 */
export const keyboardAvoidState = reactive({
  overlap: 0,
  focused: false,
  keyboardOpen: false,
});

const EDGE_PAD = 12;
const BAR_HEIGHT = 80;
/**
 * 遮挡键盘估高占比（相对满屏 baseline）。
 * 中文键盘 + 多道工具条时常用；仅 vv 测不到时兜底，不按栏数累加。
 */
const FALLBACK_KEYBOARD_RATIO = 0.65;
/** iOS 工具条安全距（约两道 accessory），只叠在 fallback 上 */
const IOS_TOOLBARS_SAFE_PX = 96;
/**
 * 锁壳已与 vv 同步时：壳高已在键盘上方，底垫只需小安全距，勿再垫整段键盘高。
 */
const VIEWPORT_LOCK_SYNCED_PAD = BAR_HEIGHT;
/** 壳高与 vv.height 差值小于此值视为已同步 */
const VIEWPORT_LOCK_SYNC_SLACK_PX = 24;
const ANDROID_RESCROLL_MS = 350;
/** 键盘动画中段再补一滚 */
const ANDROID_RESCROLL_EARLY_MS = 120;
/** iOS viewport-lock：vv.resize 落稳后再补滚，避免跟动画逐帧互抢 */
const IOS_SETTLE_MS = 140;
const BLUR_DELAY_MS = 200;
const FOCUS_SWITCH_GAP_MS = 200;
const OVERLAP_RESCROLL_THRESHOLD = 8;
const LAYOUT_SHRINK_THRESHOLD = 120;
/** 低版本窗口高度回到满屏后，再等一帧确认不是弹起动画的回弹 */
const LAYOUT_CLOSE_STABLE_MS = 180;
const OUTSIDE_TAP_SLOP_PX = 24;

const LOG_TAG = '[OpenProgress][KeyboardFocus]';

let refCount = 0;
let preferredScrollRoot: HTMLElement | null = null;
let focusTimer: ReturnType<typeof setTimeout> | null = null;
let focusTimerEarly: ReturnType<typeof setTimeout> | null = null;
let iosSettleTimer: ReturnType<typeof setTimeout> | null = null;
let lastFocusInAt = 0;
let vvRafScheduled = false;
let baselineInnerHeight = 0;
/** 这次聚焦里窗口确实矮过，才允许「高度恢复」当成键盘收起 */
let sawLayoutShrink = false;
let layoutCloseTimer: ReturnType<typeof setTimeout> | null = null;
/** 收起过程中不要再把输入框滚一次 */
let keyboardDismissing = false;
let sawVirtualKeyboard = false;
let outsideTap: { x: number; y: number } | null = null;

type VirtualKeyboardLike = {
  boundingRect?: { height: number };
  addEventListener: (type: 'geometrychange', listener: (ev: Event) => void) => void;
  removeEventListener: (type: 'geometrychange', listener: (ev: Event) => void) => void;
};

function getVirtualKeyboard(): VirtualKeyboardLike | null {
  if (typeof navigator === 'undefined') return null;
  const vk = (navigator as Navigator & { virtualKeyboard?: VirtualKeyboardLike }).virtualKeyboard;
  if (!vk || typeof vk.addEventListener !== 'function') return null;
  return vk;
}

function captureBaselineInnerHeight() {
  if (typeof window === 'undefined') return;
  const h = window.innerHeight;
  if (h > baselineInnerHeight) baselineInnerHeight = h;
}

const isPcShell = () =>
  typeof document !== 'undefined' &&
  !!document.getElementById('h5-shell')?.classList.contains('h5-shell--pc');

const isIOS = (() => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iphone|ipad|ipod/i.test(ua)) return true;
  if (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1) return true;
  return false;
})();

type ScrollAlign = 'nearest' | 'center' | 'end';

/** 低版本：scrollIntoView(options) 可能失败，退回布尔重载 */
function scrollIntoViewCompat(el: HTMLElement, align: ScrollAlign = 'nearest') {
  try {
    if (align === 'center') {
      el.scrollIntoView({ block: 'center', inline: 'nearest' });
      return;
    }
    if (align === 'end') {
      el.scrollIntoView({ block: 'end', inline: 'nearest' });
      return;
    }
    el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  } catch {
    try {
      // 布尔重载：false 尽量让元素落在可视区下方附近
      el.scrollIntoView(false);
    } catch {
      /* 依赖 scrollTop 主路径 */
    }
  }
}

function scheduleFrame(fn: () => void) {
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(fn);
  } else {
    setTimeout(fn, 16);
  }
}

function logFocus(payload: Record<string, unknown>) {
  try {
    console.log(LOG_TAG, payload);
  } catch {
    /* ignore */
  }
}

export const getKeyboardAvoidBottomReserve = () =>
  keyboardAvoidState.keyboardOpen ? 0 : BAR_HEIGHT + getEffectiveSafeBottom();

export const getBarBottomOffset = (): number => effectiveOverlapForLayout();

export function measureKeyboardOverlap(): number {
  if (typeof window === 'undefined') return 0;
  if (isAndroidWebViewKeyboardUnreliable()) return 0;
  const vv = window.visualViewport;
  if (!vv) return 0;
  // 不 cap：两道工具条 + 键盘常超过屏高 70%，截断会导致底垫/滚距不够
  return Math.max(0, Math.round(window.innerHeight - vv.height));
}

/** vv 测不到时的键盘占高；iOS 加工具条安全距（整块估算，不按栏数） */
function estimateFallbackOverlap(): number {
  const base = baselineInnerHeight || (typeof window !== 'undefined' ? window.innerHeight : 0);
  if (!base) return 0;
  const body = Math.round(base * FALLBACK_KEYBOARD_RATIO);
  return isIOS ? body + IOS_TOOLBARS_SAFE_PX : body;
}

function effectiveOverlapForLayout(): number {
  if (keyboardAvoidState.overlap > 0) return keyboardAvoidState.overlap;
  if (!keyboardAvoidState.keyboardOpen) return 0;
  const shrunk =
    baselineInnerHeight > 0 &&
    baselineInnerHeight - window.innerHeight > LAYOUT_SHRINK_THRESHOLD;
  if (shrunk) return 0;
  return estimateFallbackOverlap();
}

function computeKeyboardOpen(): boolean {
  if (isPcShell()) return false;
  if (keyboardAvoidState.overlap > 0) return true;
  if (!keyboardAvoidState.focused) return false;
  // 聚焦即开：立刻藏底栏，避免 iOS fixed 底栏盖住下方 input（不等 vv overlap）
  return true;
}

/** 锁壳节点高度是否已跟 visualViewport 对齐（父级高度问题的判定） */
function isViewportLockShellSynced(): boolean {
  if (!isOpenProgressViewportLockActive()) return false;
  const vv = window.visualViewport;
  if (!vv) return false;
  const shell = document.querySelector(
    '.open-progress.open-progress-viewport-lock',
  ) as HTMLElement | null;
  if (!shell) return false;
  return Math.abs(shell.getBoundingClientRect().height - vv.height) <= VIEWPORT_LOCK_SYNC_SLACK_PX;
}

/** 避免 #main / 文档外层和内部 scroll-root 抢滚 */
function resetOuterScroll() {
  const main = document.getElementById('main');
  if (main && main.scrollTop) main.scrollTop = 0;
  const se = document.scrollingElement as HTMLElement | null;
  if (se && se.scrollTop) se.scrollTop = 0;
}

/**
 * 找可滚祖先。Android <139：内容未溢出时也认 overflow 容器（垫 padding 后才会溢出）。
 */
function findScrollParent(el: HTMLElement, allowEmpty = false): HTMLElement | null {
  let node: HTMLElement | null = el.parentElement;
  while (node && node !== document.body) {
    const { overflowY } = getComputedStyle(node);
    const canOverflow =
      overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
    if (canOverflow && (allowEmpty || node.scrollHeight > node.clientHeight + 1)) {
      return node;
    }
    node = node.parentElement;
  }
  const main = document.getElementById('main');
  if (main && (allowEmpty || main.scrollHeight > main.clientHeight + 1)) return main;
  return (document.scrollingElement as HTMLElement) || document.documentElement;
}

function layoutShrunk(): boolean {
  return (
    baselineInnerHeight > 0 &&
    baselineInnerHeight - window.innerHeight > LAYOUT_SHRINK_THRESHOLD
  );
}

function resetLayoutShrinkWatch() {
  sawLayoutShrink = false;
  if (layoutCloseTimer) {
    clearTimeout(layoutCloseTimer);
    layoutCloseTimer = null;
  }
}

/** 用户点「完成」或空白处：忽略聚焦后的短间隔。高度恢复必须等过这个间隔，避免弹起动画被当成收起。 */
function blurActiveTextField(ignoreSwitchGap = false): boolean {
  if (keyboardDismissing) return false;
  if (!ignoreSwitchGap && Date.now() - lastFocusInAt < FOCUS_SWITCH_GAP_MS) return false;
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || !isTextField(active)) return false;
  keyboardDismissing = true;
  logFocus({ phase: 'blur-on-dismiss', tag: active.tagName, ignoreSwitchGap });
  active.blur();
  return true;
}

/**
 * 低版本 Android：视口高度不可用。窗口曾矮过再回到满屏，才失焦。
 * 返回 true 表示正在确认收起，调用方不要再滚输入框。
 */
function noteLayoutShrink(): boolean {
  if (!isAndroidWebViewKeyboardUnreliable() || !keyboardAvoidState.focused) return false;
  if (layoutShrunk()) {
    sawLayoutShrink = true;
    if (layoutCloseTimer) {
      clearTimeout(layoutCloseTimer);
      layoutCloseTimer = null;
    }
    return false;
  }
  if (!sawLayoutShrink || layoutCloseTimer) return !!layoutCloseTimer;
  layoutCloseTimer = setTimeout(() => {
    layoutCloseTimer = null;
    if (!keyboardAvoidState.focused || !sawLayoutShrink) return;
    if (layoutShrunk()) return;
    if (Date.now() - lastFocusInAt < FOCUS_SWITCH_GAP_MS) {
      noteLayoutShrink();
      return;
    }
    sawLayoutShrink = false;
    blurActiveTextField();
  }, LAYOUT_CLOSE_STABLE_MS);
  return true;
}

function onEnterKey(e: KeyboardEvent) {
  if (e.key !== 'Enter' || e.isComposing) return;
  const active = document.activeElement;
  if (!(active instanceof HTMLInputElement) || !isTextField(active)) return;
  if (!active.closest('.open-progress')) return;
  e.preventDefault();
  blurActiveTextField(true);
}

function prepareEnterKeyHint(e: Event) {
  const target = e.target;
  if (!(target instanceof Element)) return;
  const shell = target.closest('.difi-input');
  const field = target.closest('input') || shell?.querySelector('input') || null;
  if (!(field instanceof HTMLInputElement) || !isTextField(field)) return;
  if (!field.closest('.open-progress')) return;
  if (!field.enterKeyHint) field.enterKeyHint = 'done';
}

function onOutsideTouchStart(e: TouchEvent) {
  const target = e.target;
  if (!(target instanceof Element) || !target.closest('.open-progress')) {
    outsideTap = null;
    return;
  }
  if (target.closest('.difi-input, input, textarea')) {
    outsideTap = null;
    return;
  }
  const touch = e.changedTouches?.[0] ?? e.touches?.[0];
  outsideTap = touch ? { x: touch.clientX, y: touch.clientY } : null;
}

function onOutsideTouchCancel() {
  outsideTap = null;
}

function onOutsideTouchEnd(e: TouchEvent) {
  if (!outsideTap) return;
  const start = outsideTap;
  outsideTap = null;
  const touch = e.changedTouches?.[0];
  if (!touch) return;
  if (
    Math.abs(touch.clientX - start.x) > OUTSIDE_TAP_SLOP_PX ||
    Math.abs(touch.clientY - start.y) > OUTSIDE_TAP_SLOP_PX
  ) {
    return;
  }
  blurActiveTextField(true);
}

function virtualKeyboardHeight(e: Event): number {
  const fromEvent = (e.target as VirtualKeyboardLike | null)?.boundingRect?.height;
  if (typeof fromEvent === 'number') return fromEvent;
  return getVirtualKeyboard()?.boundingRect?.height ?? 0;
}

function onVirtualKeyboardGeometry(e: Event) {
  if (!isAndroidWebViewKeyboardUnreliable()) return;
  const height = virtualKeyboardHeight(e);
  if (height > 0) {
    sawVirtualKeyboard = true;
    return;
  }
  if (!sawVirtualKeyboard || !keyboardAvoidState.focused) return;
  sawVirtualKeyboard = false;
  blurActiveTextField();
}

function isTextField(el: HTMLElement): boolean {
  if (el.tagName === 'TEXTAREA') return true;
  if (el.tagName !== 'INPUT') return false;
  const type = ((el as HTMLInputElement).type || 'text').toLowerCase();
  const nonText = [
    'button',
    'checkbox',
    'radio',
    'file',
    'submit',
    'reset',
    'image',
    'hidden',
    'range',
    'color',
  ];
  return nonText.indexOf(type) === -1;
}

/**
 * 锁壳底垫：
 * - 壳已跟 vv 同步：只垫小安全距（高度模型不要叠两遍键盘高）
 * - 壳仍接近满屏：垫实测/估算整段键盘高，否则底部 input 滚不上去
 */
function viewportLockScrollPad(): number {
  if (!(keyboardAvoidState.keyboardOpen || keyboardAvoidState.focused)) return 0;
  if (isViewportLockShellSynced()) return VIEWPORT_LOCK_SYNCED_PAD;
  if (keyboardAvoidState.overlap > 0) return keyboardAvoidState.overlap;
  return estimateFallbackOverlap();
}

function applyScrollRootPadding(overlap: number) {
  if (!preferredScrollRoot) return;
  if (isOpenProgressViewportLockActive()) {
    const pad = viewportLockScrollPad();
    preferredScrollRoot.style.paddingBottom = pad > 0 ? `${pad}px` : '';
    return;
  }
  const pad =
    overlap > 0
      ? overlap
      : keyboardAvoidState.keyboardOpen || keyboardAvoidState.focused
        ? estimateFallbackOverlap()
        : 0;
  preferredScrollRoot.style.paddingBottom = pad > 0 ? `${pad}px` : '';
}

function syncOverlap() {
  if (isPcShell()) {
    if (keyboardAvoidState.overlap !== 0) keyboardAvoidState.overlap = 0;
    if (keyboardAvoidState.focused) keyboardAvoidState.focused = false;
    if (keyboardAvoidState.keyboardOpen) keyboardAvoidState.keyboardOpen = false;
    applyScrollRootPadding(0);
    return;
  }
  const overlap = measureKeyboardOverlap();
  keyboardAvoidState.overlap = overlap;
  if (overlap === 0 && !keyboardAvoidState.focused) captureBaselineInnerHeight();
  keyboardAvoidState.keyboardOpen = computeKeyboardOpen();
  applyScrollRootPadding(effectiveOverlapForLayout());
}

function clearScrollRootPadding(el: HTMLElement | null) {
  if (!el) return;
  el.style.paddingBottom = '';
}

function ensureInputVisibleNear(target: HTMLElement) {
  resetOuterScroll();
  const scrollRoot = resolveScrollRoot(target);
  const lockSynced = isViewportLockShellSynced();
  const vv = window.visualViewport;

  let visibleTop: number;
  let visibleBottom: number;

  if (lockSynced && scrollRoot) {
    // 父级壳已缩到 vv：以 scroll-root 可视矩形为准，勿再按「整段键盘」裁切
    const rootRect = scrollRoot.getBoundingClientRect();
    visibleTop = rootRect.top + EDGE_PAD;
    visibleBottom = rootRect.bottom - EDGE_PAD;
  } else {
    visibleTop = vv ? vv.offsetTop + EDGE_PAD : EDGE_PAD;
    visibleBottom = vv
      ? vv.offsetTop + vv.height - EDGE_PAD
      : window.innerHeight - EDGE_PAD;
    if (isIOS && keyboardAvoidState.focused) {
      if (keyboardAvoidState.overlap <= 0) {
        visibleBottom -= estimateFallbackOverlap();
      } else {
        visibleBottom -= EDGE_PAD;
      }
    }
  }

  const rect = target.getBoundingClientRect();
  if (!(rect.bottom > visibleBottom || rect.top < visibleTop)) {
    logFocus({
      phase: 'ensureVisibleNear-skip',
      lockSynced,
      rectBottom: Math.round(rect.bottom),
      visibleBottom: Math.round(visibleBottom),
      shellH: Math.round(
        document
          .querySelector('.open-progress.open-progress-viewport-lock')
          ?.getBoundingClientRect().height ?? 0,
      ),
      vvH: vv ? Math.round(vv.height) : null,
    });
    return;
  }

  // 优先滚内部 scroll-root，避免 iOS 上 scrollIntoView 带动 visualViewport 再触发 scroll 风暴
  if (scrollRoot) {
    let delta = 0;
    if (rect.bottom > visibleBottom) delta = rect.bottom - visibleBottom;
    else if (rect.top < visibleTop) delta = -(visibleTop - rect.top);
    if (delta) {
      scrollRoot.scrollTop += delta;
      logFocus({
        phase: 'ensureVisibleNear-scroll',
        lockSynced,
        delta: Math.round(delta),
        scrollTop: Math.round(scrollRoot.scrollTop),
        paddingBottom: scrollRoot.style.paddingBottom || '',
      });
      return;
    }
  }
  scrollIntoViewCompat(target, 'nearest');
}

/** iOS + viewport-lock：resize 结束后再 ensure 一次，不跟动画逐帧滚 */
function scheduleIosViewportSettleEnsure() {
  if (!isIOS || !isOpenProgressViewportLockActive()) return;
  if (iosSettleTimer) clearTimeout(iosSettleTimer);
  iosSettleTimer = setTimeout(() => {
    iosSettleTimer = null;
    if (keyboardDismissing) return;
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || !isTextField(active)) return;
    syncOverlap();
    ensureInputVisible(active);
    const shell = document.querySelector(
      '.open-progress.open-progress-viewport-lock',
    ) as HTMLElement | null;
    const vv = window.visualViewport;
    logFocus({
      phase: 'ios-settle-ensure',
      overlap: keyboardAvoidState.overlap,
      keyboardOpen: keyboardAvoidState.keyboardOpen,
      lockSynced: isViewportLockShellSynced(),
      shellH: shell ? Math.round(shell.getBoundingClientRect().height) : null,
      vvH: vv ? Math.round(vv.height) : null,
      paddingBottom: preferredScrollRoot?.style.paddingBottom || '',
      scrollTop: preferredScrollRoot?.scrollTop ?? null,
      scrollHeight: preferredScrollRoot?.scrollHeight ?? null,
      clientHeight: preferredScrollRoot?.clientHeight ?? null,
    });
  }, IOS_SETTLE_MS);
}

function resolveScrollRoot(target: HTMLElement): HTMLElement | null {
  if (preferredScrollRoot && preferredScrollRoot.contains(target)) {
    return preferredScrollRoot;
  }
  // <139：允许尚未溢出的 overflow 容器（先垫 padding 再滚）
  return findScrollParent(target, isAndroidWebViewKeyboardUnreliable());
}

/**
 * 把 input 滚到「键盘上方」安全区。
 * Android <139 遮挡键盘：布局高度不变，nearest 常认为已可见 → 必须用估算底边 + center 兜底。
 */
function ensureInputVisible(target: HTMLElement) {
  if (isOpenProgressViewportLockActive()) {
    // 保证底垫已写入，再量几何滚位
    applyScrollRootPadding(effectiveOverlapForLayout());
    ensureInputVisibleNear(target);
    return;
  }
  if (isIOS) return;

  const unreliable = isAndroidWebViewKeyboardUnreliable();
  // 先同步 padding，再量几何，否则 scrollHeight 不够滚
  syncOverlap();

  const scrollRoot = resolveScrollRoot(target);
  const vv = window.visualViewport;
  const useVvBox = !!vv && !unreliable;
  const fallbackCut =
    keyboardAvoidState.overlap <= 0 && keyboardAvoidState.keyboardOpen
      ? estimateFallbackOverlap()
      : 0;
  const visibleTop = useVvBox ? vv!.offsetTop : 0;
  const layoutBottom = useVvBox ? vv!.offsetTop + vv!.height : window.innerHeight;
  const visibleBottom = layoutBottom - fallbackCut;
  const reserve = getKeyboardAvoidBottomReserve() + EDGE_PAD;
  const limitBottom = visibleBottom - reserve;
  const limitTop = visibleTop + EDGE_PAD;
  const rectBefore = target.getBoundingClientRect();

  let delta = 0;
  if (scrollRoot) {
    if (rectBefore.bottom > limitBottom) {
      delta = rectBefore.bottom - limitBottom;
    } else if (rectBefore.top < limitTop) {
      delta = -(limitTop - rectBefore.top);
    }
    // 遮挡键盘：即便刚好贴边，也再上推一点，避免被键盘上沿挡住
    if (unreliable && keyboardAvoidState.keyboardOpen && delta === 0) {
      const midSafe = (limitTop + limitBottom) / 2;
      if (rectBefore.bottom > midSafe) {
        delta = Math.round(rectBefore.bottom - midSafe);
      }
    }
    if (delta) {
      scrollRoot.scrollTop += delta;
    }
  }

  // 兜底：遮挡场景 nearest 无效，改用 center；其它仍 nearest
  if (keyboardAvoidState.keyboardOpen && keyboardAvoidState.overlap <= 0) {
    scheduleFrame(() => {
      if (document.activeElement !== target) return;
      scrollIntoViewCompat(target, unreliable ? 'center' : 'nearest');
    });
  }

  const rectAfter = target.getBoundingClientRect();
  logFocus({
    phase: 'ensureVisible',
    tag: target.tagName,
    unreliable,
    keyboardOpen: keyboardAvoidState.keyboardOpen,
    overlap: keyboardAvoidState.overlap,
    fallbackCut,
    hasPreferredRoot: !!preferredScrollRoot,
    preferredContains: !!(preferredScrollRoot && preferredScrollRoot.contains(target)),
    scrollRootTag: scrollRoot?.tagName ?? null,
    scrollRootClass: scrollRoot?.className?.toString?.().slice(0, 80) ?? null,
    scrollTop: scrollRoot?.scrollTop ?? null,
    clientHeight: scrollRoot?.clientHeight ?? null,
    scrollHeight: scrollRoot?.scrollHeight ?? null,
    paddingBottom: preferredScrollRoot?.style.paddingBottom || '',
    delta,
    limitBottom,
    rectBottomBefore: Math.round(rectBefore.bottom),
    rectBottomAfter: Math.round(rectAfter.bottom),
    innerHeight: window.innerHeight,
  });
}

function clearFocusTimers() {
  if (focusTimer) clearTimeout(focusTimer);
  if (focusTimerEarly) clearTimeout(focusTimerEarly);
  if (iosSettleTimer) clearTimeout(iosSettleTimer);
  focusTimer = null;
  focusTimerEarly = null;
  iosSettleTimer = null;
}

function onFocusIn(e: FocusEvent) {
  if (isPcShell()) return;
  const t = e.target;
  if (!(t instanceof HTMLElement)) return;
  if (!isTextField(t)) {
    logFocus({
      phase: 'focusin-skip',
      tag: t.tagName,
      type: (t as HTMLInputElement).type || '',
      reason: 'not-text-field',
    });
    return;
  }

  lastFocusInAt = Date.now();
  clearFocusTimers();
  keyboardDismissing = false;
  resetLayoutShrinkWatch();
  sawVirtualKeyboard = false;
  keyboardAvoidState.focused = true;
  syncOverlap();

  logFocus({
    phase: 'focusin',
    tag: t.tagName,
    type: (t as HTMLInputElement).type || 'text',
    keyboardOpen: keyboardAvoidState.keyboardOpen,
    lockSynced: isViewportLockShellSynced(),
    unreliable: isAndroidWebViewKeyboardUnreliable(),
    hasPreferredRoot: !!preferredScrollRoot,
    paddingBottom: preferredScrollRoot?.style.paddingBottom || '',
    innerHeight: window.innerHeight,
    vvH: window.visualViewport ? Math.round(window.visualViewport.height) : null,
  });

  if (isIOS && !isOpenProgressViewportLockActive()) return;

  const run = () => {
    if (document.activeElement === t) ensureInputVisible(t);
  };
  scheduleFrame(run);

  // iOS + viewport-lock：动画中途反复滚会与 vv.scroll/resize 互抢导致页面抖动；
  // 只在键盘大致落稳后再补一次。
  if (isIOS && isOpenProgressViewportLockActive()) {
    focusTimer = setTimeout(() => {
      focusTimer = null;
      if (document.activeElement === t) {
        syncOverlap();
        ensureInputVisible(t);
      }
    }, ANDROID_RESCROLL_MS);
    return;
  }

  // Android：键盘动画中段 + 末尾各补一次（WebView 103 遮挡键盘常见延迟）
  focusTimerEarly = setTimeout(() => {
    focusTimerEarly = null;
    run();
  }, ANDROID_RESCROLL_EARLY_MS);
  focusTimer = setTimeout(() => {
    focusTimer = null;
    if (document.activeElement === t) {
      syncOverlap();
      ensureInputVisible(t);
    }
  }, ANDROID_RESCROLL_MS);
}

function onFocusOut() {
  clearFocusTimers();
  focusTimer = setTimeout(() => {
    focusTimer = null;
    if (Date.now() - lastFocusInAt < FOCUS_SWITCH_GAP_MS) return;
    const active = document.activeElement;
    if (active instanceof HTMLElement && isTextField(active)) return;
    keyboardDismissing = false;
    resetLayoutShrinkWatch();
    sawVirtualKeyboard = false;
    keyboardAvoidState.focused = false;
    syncOverlap();
  }, BLUR_DELAY_MS);
}

function onViewportResize() {
  if (isPcShell()) {
    syncOverlap();
    return;
  }
  if (vvRafScheduled) return;
  vvRafScheduled = true;
  scheduleFrame(() => {
    vvRafScheduled = false;
    const prevOverlap = keyboardAvoidState.overlap;
    syncOverlap();
    const nextOverlap = keyboardAvoidState.overlap;
    const unreliable = isAndroidWebViewKeyboardUnreliable();
    // 高版本：视口从变矮恢复到接近全屏，视为键盘收起。弹起（变矮）不走这里。
    const closingByOverlap =
      !unreliable &&
      prevOverlap > OVERLAP_RESCROLL_THRESHOLD &&
      nextOverlap <= OVERLAP_RESCROLL_THRESHOLD;
    if (closingByOverlap && blurActiveTextField()) return;
    if (keyboardDismissing) return;
    // 低版本：窗口高度恢复时失焦；确认收起的那一帧不再滚
    if (unreliable && noteLayoutShrink()) return;
    // iOS + viewport-lock：不逐帧滚，debounce 到键盘落稳后再 ensure 一次
    if (isIOS && isOpenProgressViewportLockActive()) {
      if (keyboardAvoidState.focused) scheduleIosViewportSettleEnsure();
      return;
    }
    const overlapDelta = Math.abs(nextOverlap - prevOverlap);
    const needRescroll =
      overlapDelta > OVERLAP_RESCROLL_THRESHOLD ||
      (unreliable && keyboardAvoidState.keyboardOpen);
    if (needRescroll) {
      const active = document.activeElement;
      if (active instanceof HTMLElement && isTextField(active)) {
        ensureInputVisible(active);
      }
    }
  });
}

function onViewportScroll() {
  if (isPcShell()) return;
  // 仅 viewport-lock 需要跟 vv.scroll；iOS 上 ensure 会与 vv 互抢导致持续抖动
  if (!isOpenProgressViewportLockActive()) return;
  if (isIOS) return;
  const active = document.activeElement;
  if (active instanceof HTMLElement && isTextField(active)) {
    ensureInputVisible(active);
  }
}

export function setKeyboardAvoidScrollRoot(el: HTMLElement | null) {
  if (preferredScrollRoot === el) {
    syncOverlap();
    return;
  }
  clearScrollRootPadding(preferredScrollRoot);
  preferredScrollRoot = el;
  syncOverlap();
  logFocus({
    phase: 'scrollRoot',
    set: !!el,
    className: el?.className?.toString?.().slice(0, 80) ?? null,
    paddingBottom: el?.style.paddingBottom || '',
  });
}

export function attachKeyboardAvoid(): () => void {
  if (!canUseKeyboardAvoidJs()) {
    logFocus({ phase: 'attach-skip', reason: 'canUseKeyboardAvoidJs=false' });
    return () => {};
  }
  refCount += 1;
  if (refCount === 1 && typeof window !== 'undefined') {
    captureBaselineInnerHeight();
    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', onFocusOut, true);
    document.addEventListener('keydown', onEnterKey, true);
    document.addEventListener('touchstart', prepareEnterKeyHint, true);
    document.addEventListener('touchstart', onOutsideTouchStart, true);
    document.addEventListener('touchend', onOutsideTouchEnd, true);
    document.addEventListener('touchcancel', onOutsideTouchCancel, true);
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', onViewportResize);
      vv.addEventListener('scroll', onViewportScroll);
    }
    window.addEventListener('resize', onViewportResize);
    getVirtualKeyboard()?.addEventListener('geometrychange', onVirtualKeyboardGeometry);
    logFocus({
      phase: 'attach',
      unreliable: isAndroidWebViewKeyboardUnreliable(),
      hasVisualViewport: !!vv,
      baselineInnerHeight,
      innerHeight: window.innerHeight,
    });
  }
  return () => {
    refCount = Math.max(0, refCount - 1);
    if (refCount === 0) {
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('focusout', onFocusOut, true);
      document.removeEventListener('keydown', onEnterKey, true);
      document.removeEventListener('touchstart', prepareEnterKeyHint, true);
      document.removeEventListener('touchstart', onOutsideTouchStart, true);
      document.removeEventListener('touchend', onOutsideTouchEnd, true);
      document.removeEventListener('touchcancel', onOutsideTouchCancel, true);
      const vv = window.visualViewport;
      if (vv) {
        vv.removeEventListener('resize', onViewportResize);
        vv.removeEventListener('scroll', onViewportScroll);
      }
      window.removeEventListener('resize', onViewportResize);
      getVirtualKeyboard()?.removeEventListener('geometrychange', onVirtualKeyboardGeometry);
      clearFocusTimers();
      resetLayoutShrinkWatch();
      keyboardDismissing = false;
      sawVirtualKeyboard = false;
      outsideTap = null;
      clearScrollRootPadding(preferredScrollRoot);
      preferredScrollRoot = null;
      keyboardAvoidState.overlap = 0;
      keyboardAvoidState.focused = false;
      keyboardAvoidState.keyboardOpen = false;
      vvRafScheduled = false;
      baselineInnerHeight = 0;
      logFocus({ phase: 'detach' });
    }
  };
}
